// src/components/toolkit/IngredientsChecker.jsx — goes with Module 3.
//
// Write a story the way you tell it now, then check it against the module's
// five ingredients. The point is the gaps: the tool shows what the story is
// missing and why that ingredient matters, using the module's own wording.
import { useState } from 'react';
import { ArrowLeft, Check, ListChecks } from 'lucide-react';
import { STORY_INGREDIENTS } from '../../data/toolkitData';
import { loadTool, saveTool } from './toolStorage';

const TOOL_ID = 'ingredients-checker';
const card = 'rounded-xl border border-thread-border bg-white/5 p-5';

const IngredientsChecker = ({ onBack, onComplete }) => {
  const [state, setState] = useState(() => loadTool(TOOL_ID, { story: '', present: [], done: false }));
  const present = state.present || [];

  const update = (next) => {
    const merged = { ...state, ...next };
    setState(merged);
    saveTool(TOOL_ID, merged);
  };

  const toggle = (name) => {
    update({ present: present.includes(name) ? present.filter((n) => n !== name) : [...present, name] });
  };

  const missing = STORY_INGREDIENTS.filter((ingredient) => !present.includes(ingredient.name));

  const finish = () => {
    update({ done: true });
    onComplete?.({ present: present.length, missing: missing.length });
  };

  return (
    <div className="min-h-screen bg-thread-darker text-white pt-36 pb-20">
      <div className="container mx-auto px-4 max-w-3xl">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-2 min-h-[44px] text-thread-sand hover:text-white mb-6"
        >
          <ArrowLeft className="w-4 h-4" aria-hidden="true" /> Back to the toolkit
        </button>

        <h1 className="text-3xl md:text-4xl font-bold font-display mb-3">Five ingredients checker</h1>
        <p className="text-slate-300 mb-8">
          Pick a family story you already tell. Write it roughly as you would say it out loud, then be honest about what
          is in it. Missing ingredients are not a failure: they are the next draft.
        </p>

        <label className="block mb-8">
          <span className="block font-semibold text-white mb-2">The story, as you tell it now</span>
          <textarea
            value={state.story || ''}
            onChange={(e) => update({ story: e.target.value })}
            rows={7}
            placeholder="The year Dad lost his job, we..."
            className="w-full rounded-xl border border-white/20 bg-white/5 px-4 py-3 text-white placeholder:text-slate-500 focus:border-thread-glow focus:outline-none"
          />
          <span className="block text-sm text-slate-400 mt-2">Saved on this device as you type.</span>
        </label>

        <h2 className="text-2xl font-bold mb-3">What is in it?</h2>
        <ul className="space-y-3 mb-8">
          {STORY_INGREDIENTS.map((ingredient) => {
            const active = present.includes(ingredient.name);
            return (
              <li key={ingredient.name} className={card}>
                <button
                  type="button"
                  onClick={() => toggle(ingredient.name)}
                  aria-pressed={active}
                  className="flex items-start gap-3 text-left w-full"
                >
                  <span
                    className={`mt-1 w-5 h-5 shrink-0 rounded border flex items-center justify-center ${
                      active ? 'border-thread-glow bg-thread-ember text-white' : 'border-white/30'
                    }`}
                    aria-hidden="true"
                  >
                    {active && <Check className="w-3.5 h-3.5" />}
                  </span>
                  <span>
                    <span className="font-semibold text-white">
                      <span aria-hidden="true">{ingredient.symbol} </span>
                      {ingredient.name}
                    </span>
                    <span className="block text-slate-300">{ingredient.description}</span>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>

        {(state.story || present.length > 0) && (
          <section aria-label="What the story is missing" className="mb-8">
            <h2 className="flex items-center gap-2 text-2xl font-bold mb-3">
              <ListChecks className="w-6 h-6 text-thread-glow" aria-hidden="true" />
              {missing.length === 0 ? 'All five are in there' : `Missing: ${missing.length} of 5`}
            </h2>
            {missing.length === 0 ? (
              <p className="text-slate-300">
                Then the next step is telling it out loud to someone who was there, and seeing what they add.
              </p>
            ) : (
              <div className="space-y-4">
                {missing.map((ingredient) => (
                  <div key={ingredient.name} className={card}>
                    <h3 className="font-semibold text-white mb-1">
                      <span aria-hidden="true">{ingredient.symbol} </span>
                      {ingredient.name}
                    </h3>
                    <p className="text-slate-300 mb-2">{ingredient.whyItMatters?.[0]}</p>
                    {ingredient.whatItIncludes?.[0] && (
                      <p className="text-sm text-thread-sand">To add it: {ingredient.whatItIncludes[0].toLowerCase()}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        <button
          type="button"
          onClick={finish}
          disabled={!state.story && present.length === 0}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-thread-ember hover:bg-thread-rust disabled:opacity-40 text-white font-semibold transition-colors"
        >
          <Check className="w-4 h-4" aria-hidden="true" /> {state.done ? 'Saved' : 'Save this check'}
        </button>
      </div>
    </div>
  );
};

export default IngredientsChecker;
